import {Observable} from 'rxjs/Observable';
import {PointsEstimationDao} from '../daos/points-estimation.dao';
import {Reservation} from '../model/reservation/reservation.model';
import {Injectable} from '@angular/core';
import {LogService} from './logging.service';
import {PointsEstimateBuilder} from './builders/points-estimate.model.builder';
import {EarningsEstimate} from '../model/reservation/points-estimate';
import {IPointsEstimationParams} from '../interfaces';
import {DateUtils} from './../../common/date.utils';
import {CookieService} from 'ngx-cookie-service';
import {useDefault} from '../../common/common';
import {BaseService} from './base.service';
import {ServiceErrorMapper} from './mapper/service-error.mapper';
import {ServiceError} from '../model/errors/service-error.model';

const POINTS_ESTIMATE_DURATION = 10 * 60 * 1000; // 10 mins
const CLASS_NAME = 'PointsEstimateService';

const SSO_TOKEN_COOKIE_NAME = 'X-IHG-SSO-TOKEN';

@Injectable()
export class PointsEstimateService extends BaseService <EarningsEstimate, Reservation> {
  private _pointsEstimateObservable: Map<string, Observable<EarningsEstimate>>;

  constructor(
    private _pointsEstimationDao: PointsEstimationDao,
    private _log: LogService,
    private _cookieService: CookieService
  ) {
    super(new ServiceErrorMapper(), POINTS_ESTIMATE_DURATION);
    this._pointsEstimateObservable = new Map<string, Observable<EarningsEstimate>>();
  }

  getReservationPointsEstimate(reservation: Reservation, useCache: boolean = false): Observable<EarningsEstimate> {
    if (!reservation) {
      return Observable.of(new EarningsEstimate());
    }
    return this.getByKey(reservation, useCache);
  }

  protected getFreshObservable(reservation: Reservation): Observable<EarningsEstimate> {
    return this._pointsEstimationDao.get(this._createPointsEstimationRequest(reservation))
    .map((pointsEstimateData) => {
      return new PointsEstimateBuilder().withPointsEstimateData(pointsEstimateData).build();
    }).catch((error) => {
      const serviceError: ServiceError = this.mapError(error);
      this._log.error(`Error when getting points estimate for reservation ${reservation.confirmationNumber}`, CLASS_NAME, serviceError);
      return Observable.throw(serviceError);
    });
  }
  protected generateKey(reservation: Reservation) {
    return [reservation.confirmationNumber,
      reservation.guest.lastName,
      reservation.rate.code,
      DateUtils.toISODate(reservation.stay.checkInDate),
      DateUtils.toISODate(reservation.stay.checkOutDate),
      reservation.stay.totalPrice,
      reservation.rate.charges.taxes,
      useDefault(this._cookieService.get(SSO_TOKEN_COOKIE_NAME), '')].join('-');
  }

  private _createPointsEstimationRequest(reservation: Reservation): IPointsEstimationParams {
    let queryParameters = <IPointsEstimationParams>{};

    queryParameters.hotelCode = reservation.hotelMnemonic;
    queryParameters.rateCode = reservation.rate.code;
    queryParameters.currencyCode = reservation.rate.currencyCode;
    queryParameters.checkInDate = DateUtils.toISODate(reservation.stay.checkInDate);
    queryParameters.checkOutDate = DateUtils.toISODate(reservation.stay.checkOutDate);
    queryParameters.totalRoomRevenue = reservation.stay.totalPrice - reservation.rate.charges.taxes;

    if (this._cookieService.get(SSO_TOKEN_COOKIE_NAME)) {
      queryParameters.programCode = 'PC';
    }

    return queryParameters;

  }
}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/api/services/points-estimate.service.ts